import React, { useRef } from 'react';
import { useQuery, QueryFunctionContext } from 'react-query';
import WeatherSkeleton from './Skeletons/WeatherSkeleton';

interface WeatherEntry {
  date: string;
  weather: string;
  summary: string;
  icon: number;
  temperature: number;
  feels_like: number;
}

interface QueryKey {
  startDate: string;
  latitude: number;
  longitude: number;
}

type QueryKeyTuple = [string, QueryKey];

const adjustDateYear = (date: Date, targetYear: number): Date => {
  const newDate = new Date(date);
  newDate.setFullYear(targetYear);
  return newDate;
};

const fetchWeatherData = async ({ queryKey }: QueryFunctionContext<QueryKeyTuple>): Promise<WeatherEntry[]> => {
  const [_key, { startDate, latitude, longitude }] = queryKey;
  let date = adjustDateYear(new Date(startDate), 2023);
  let results: WeatherEntry[] = [];

  for (let i = 0; i < 12; i++) {
    const dateString = date.toISOString().split('T')[0];
    const url = `https://ai-weather-by-meteosource.p.rapidapi.com/time_machine?date=${dateString}&lat=${latitude}&lon=${longitude}&units=metric`;
    const options = {
      method: 'GET',
      headers: {
        'X-RapidAPI-Key': 'cc5fe4c4a1mshef203a57fb002acp1bbab7jsn2e52ddd7c2a1',
        'X-RapidAPI-Host': 'ai-weather-by-meteosource.p.rapidapi.com'
      }
    };

    const response = await fetch(url, options);
    if (!response.ok) throw new Error('Network response was not ok');
    const data = await response.json();

    // console.log('API Response:', data);

    if (data && data.data && data.data.length > 0) {
      const totalEntries = data.data.length;
      const avgTemp = data.data.reduce((sum: number, entry: any) => sum + entry.temperature, 0) / totalEntries;
      const avgFeelsLike = data.data.reduce((sum: number, entry: any) => sum + entry.temperature, 0) / totalEntries;
      
      results.push({ 
        date: dateString, 
        weather: data.data[0].weather,  // Take the first entry's weather description
        summary: data.data[0].summary,  // Take the first entry's summary
        icon: data.data[0].icon,  // Take the first entry's icon
        temperature: avgTemp, 
        feels_like: avgFeelsLike 
      });
    } else {
      console.error(`Unexpected API response structure for date: ${dateString}`);
    }

    date.setMonth(date.getMonth() + 1);
  }

  return results;
};


const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  // date.setFullYear(year);
  const options: Intl.DateTimeFormatOptions = { month: 'short' };
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

interface WeatherComponentProps {
  startDate: string;
  latitude: number;
  longitude: number;
}

const mockWeatherData: WeatherEntry[] = [
  // {
  //   date: "2024-08-20",
  //   weather: "overcast",
  //   summary: "Overcast",
  //   icon: 7,
  //   temperature: 26.7,
  //   feels_like: 25.9
  // },
  // {
  //   date: "2024-09-20",
  //   weather: "sunny",
  //   summary: "Clear sky",
  //   icon: 1,
  //   temperature: 30.2,
  //   feels_like: 31.5
  // },
  // Add more mock entries as needed
];

const WeatherComponentMonth: React.FC<WeatherComponentProps> = ({ startDate, latitude, longitude }) => {
  // const originalYear = new Date(startDate).getFullYear();
  const { data = mockWeatherData, error, isLoading } = useQuery(['weatherData', { startDate, latitude, longitude }], fetchWeatherData);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth',
      });
    }
  };

  if (isLoading) return <WeatherSkeleton />;
  if (error) return <div className='text-red-500 font-semibold'>Error: {(error as Error).message}</div>;
  if (!data) return <div>No data available</div>;

  return (
    <div className="relative flex flex-col items-start w-full lg:px-0 mt-5">
      <div className="relative flex overflow-hidden w-full mb-11 p-2 bg-white rounded-lg shadow-sm">
        <button
          className="absolute left-0 top-1/2 transform -translate-y-1/2 p-2 bg-gray-200 rounded-full shadow-md hover:bg-halo-pink hover:text-white transition-colors"
          onClick={scrollLeft}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <button
          className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 bg-gray-200 rounded-full shadow-md hover:bg-halo-pink hover:text-white transition-colors"
          onClick={scrollRight}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto space-x-2 scrollbar-hide"
        >
          {data.map((entry, index) => (
            <div className="min-w-[180px] flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm hover:shadow-md transition-shadow" key={index}>
              <span className="text-lg font-medium text-gray-600">{formatDate(entry.date)}</span>
              <img
                src={`/weatherIcons/${entry.icon}.svg`}
                alt={entry.weather}
                className="w-16 h-16 my-2"
              />
              <p className="text-gray-800 font-medium">{entry.summary}</p>
              <p className="text-gray-600">Temperature: {entry.temperature.toFixed(1)}°C</p>
              <p className="text-gray-600">Feels Like: {entry.feels_like.toFixed(1)}°C</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeatherComponentMonth;